import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, CreditCard, CheckCircle, AlertCircle, Trash2, Plus } from 'lucide-react';
import PaymentSettingsForm from '../components/PaymentSettingsForm';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { FEE_PAYERS, FEE_TYPES, PAYMENT_COLLECTION_MODES, PAYMENT_PROVIDERS, PROVIDER_LABELS, SETTLEMENT_MODES, feeBreakdown, isMerchantReadyFor } from '../utils/cashfree';
import { formatDateTime } from '../utils/datetime';
import { formatCurrency } from '../utils/currency';
import { useEventRoute } from '../utils/eventRef';

const DEFAULT_CONFIG = {
  mode: PAYMENT_COLLECTION_MODES.OFFLINE,
  provider: PAYMENT_PROVIDERS.CASHFREE,
  merchantId: null,
  settlement: SETTLEMENT_MODES.SPLIT,
  feeType: FEE_TYPES.NONE,
  feePercent: null,
  feeFlat: null,
  feeCap: null,
  feePayer: FEE_PAYERS.ATTENDEE,
  autoApproveOnPayment: true,
};

const numberOrNull = (value) => (value === '' || value == null ? null : Number(value));

/** Mode, receiving account and platform fee; saved together. */
const CollectionCard = ({ eventId, event, view, merchants, platform }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [draft, setDraft] = useState(DEFAULT_CONFIG);
  useEffect(() => {
    setDraft({ ...DEFAULT_CONFIG, ...(view?.config || {}) });
  }, [view]);
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const save = useMutation({
    mutationFn: () => ApiService.updateEventPaymentConfig(eventId, {
      ...draft,
      feePercent: numberOrNull(draft.feePercent),
      feeFlat: numberOrNull(draft.feeFlat),
      feeCap: numberOrNull(draft.feeCap),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventPaymentConfig', eventId] });
      showSuccess('Payment collection saved');
    },
    onError: (err) => showError(err?.message || 'Failed to save'),
  });

  const online = draft.mode === PAYMENT_COLLECTION_MODES.ONLINE;
  const provider = draft.provider || PAYMENT_PROVIDERS.CASHFREE;
  const providerName = PROVIDER_LABELS[provider];
  const providers = view?.providers || { CASHFREE: !!platform?.configured, PAYU: !!platform?.payu?.configured };
  const collect = draft.settlement === SETTLEMENT_MODES.COLLECT;
  const selectable = merchants.filter((m) => m.enabled !== false);
  const selected = merchants.find((m) => m.id === draft.merchantId);
  const selectedReady = selected && isMerchantReadyFor(selected, provider);
  const samplePrice = Number(event?.price) > 0 ? Number(event.price) : 1000;
  const sample = feeBreakdown(draft, samplePrice);
  const currency = event?.currency || 'INR';
  const showPercent = draft.feeType === FEE_TYPES.PERCENT || draft.feeType === FEE_TYPES.PERCENT_PLUS_FLAT;
  const showFlat = draft.feeType === FEE_TYPES.FLAT || draft.feeType === FEE_TYPES.PERCENT_PLUS_FLAT;

  return (
    <Card>
      <CardHeader><CardTitle>How this event collects payments</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 md:grid-cols-2">
          {[
            [PAYMENT_COLLECTION_MODES.OFFLINE, 'Offline, manual approval', 'Attendees pay to your bank/UPI and upload the transaction ID and screenshot. You approve each registration.'],
            [PAYMENT_COLLECTION_MODES.ONLINE, 'Online payment', 'Cards, UPI, net banking and wallets through Cashfree or PayU. Registrations are confirmed automatically when the payment succeeds.'],
          ].map(([value, title, text]) => (
            <label key={value} className={`rounded-lg border p-4 cursor-pointer ${draft.mode === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 font-medium">
                <input type="radio" name="mode" checked={draft.mode === value} onChange={() => set({ mode: value })} /> {title}
              </div>
              <p className="text-sm text-gray-600 mt-1">{text}</p>
            </label>
          ))}
        </div>

        {online && (
          <>
            <div className="space-y-2">
              <Label>Payment gateway</Label>
              <div className="flex flex-wrap gap-3">
                {Object.values(PAYMENT_PROVIDERS).map((value) => (
                  <label key={value} className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm cursor-pointer ${provider === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
                    <input type="radio" name="provider" checked={provider === value} onChange={() => set({ provider: value })} />
                    {PROVIDER_LABELS[value]}
                    <span className={`rounded-full px-2 py-0.5 text-xs ${providers[value] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {providers[value] ? 'connected' : 'not set up'}
                    </span>
                  </label>
                ))}
              </div>
              {!providers[provider] && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  {providerName} credentials aren't set on the server yet, so online mode with {providerName} can't be saved.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Gateway per currency (premium registration)</Label>
              <p className="text-xs text-gray-500">Passes priced in another currency (e.g. USD for foreign delegates) can use a different gateway. PayU here charges INR only, so map other currencies to Cashfree with international payments enabled.</p>
              <div className="flex flex-wrap gap-3">
                {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'].map((currency) => (
                  <label key={currency} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                    <span className="w-10 font-medium">{currency}</span>
                    <select className="rounded border border-gray-300 px-2 py-1 text-sm"
                      value={draft.providerByCurrency?.[currency] || ''}
                      onChange={(e) => {
                        const next = { ...(draft.providerByCurrency || {}) };
                        if (e.target.value) next[currency] = e.target.value; else delete next[currency];
                        set({ providerByCurrency: Object.keys(next).length ? next : null });
                      }}>
                      <option value="">Default ({providerName})</option>
                      {Object.values(PAYMENT_PROVIDERS).map((value) => <option key={value} value={value}>{PROVIDER_LABELS[value]}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Money goes to</Label>
              <select className="h-10 w-full max-w-md rounded-md border border-gray-300 px-2 text-sm"
                value={draft.merchantId || ''} onChange={(e) => set({ merchantId: e.target.value || null })}>
                <option value="">Platform account (default)</option>
                {selectable.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{isMerchantReadyFor(m, provider) ? '' : ` (not set up on ${providerName})`}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                <Link className="underline" to="/admin/organizers">Manage merchants</Link>
              </p>
            </div>

            {draft.merchantId && (
              <div className="space-y-2">
                <Label>How the merchant is paid</Label>
                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    [SETTLEMENT_MODES.SPLIT, 'Split at payment', `${providerName} splits every payment: the merchant's share settles to their bank automatically and the platform keeps the fee.`],
                    [SETTLEMENT_MODES.COLLECT, 'Collect, transfer later', 'The full amount settles to the platform account. You transfer the merchant\'s share later from this page, through the gateway or by bank transfer.'],
                  ].map(([value, title, text]) => (
                    <label key={value} className={`rounded-lg border p-3 cursor-pointer ${draft.settlement === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <input type="radio" name="settlement" checked={draft.settlement === value} onChange={() => set({ settlement: value })} /> {title}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{text}</p>
                    </label>
                  ))}
                </div>
                {selected && !selectedReady && (
                  <p className="text-sm text-amber-700">
                    {provider === PAYMENT_PROVIDERS.PAYU
                      ? `${selected.name} has no PayU child merchant key yet. ${collect ? 'Transfers will have to be recorded manually until it does.' : 'PayU can\'t split to it until you add the key on the Payment Merchants page.'}`
                      : `${selected.name} isn't an active Cashfree vendor yet. ${collect ? 'Transfers will have to be recorded manually until it is.' : 'Checkout stays closed until it is.'}`}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Label>Platform fee</Label>
              <div className="flex flex-wrap gap-3 items-end">
                <select className="h-10 rounded-md border border-gray-300 px-2 text-sm" value={draft.feeType}
                  onChange={(e) => set({ feeType: e.target.value })}>
                  <option value={FEE_TYPES.NONE}>No fee</option>
                  <option value={FEE_TYPES.FLAT}>Flat amount</option>
                  <option value={FEE_TYPES.PERCENT}>Percentage</option>
                  <option value={FEE_TYPES.PERCENT_PLUS_FLAT}>Percentage + flat</option>
                </select>
                {showPercent && (
                  <label className="text-sm">% <Input className="w-24 inline-block" type="number" min="0" max="50" step="0.01"
                    value={draft.feePercent ?? ''} onChange={(e) => set({ feePercent: e.target.value })} /></label>
                )}
                {showFlat && (
                  <label className="text-sm">{currency} <Input className="w-28 inline-block" type="number" min="0" step="0.01"
                    value={draft.feeFlat ?? ''} onChange={(e) => set({ feeFlat: e.target.value })} /></label>
                )}
                {draft.feeType !== FEE_TYPES.NONE && (
                  <label className="text-sm">Max {currency} <Input className="w-28 inline-block" type="number" min="0" step="0.01"
                    value={draft.feeCap ?? ''} placeholder="no cap" onChange={(e) => set({ feeCap: e.target.value })} /></label>
                )}
              </div>
              {draft.feeType !== FEE_TYPES.NONE && (
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={draft.feePayer === FEE_PAYERS.ATTENDEE} onChange={() => set({ feePayer: FEE_PAYERS.ATTENDEE })} />
                    Attendee pays it (added as a convenience fee)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={draft.feePayer === FEE_PAYERS.ORGANIZER} onChange={() => set({ feePayer: FEE_PAYERS.ORGANIZER })} />
                    Organizer pays it (deducted from the payout)
                  </label>
                </div>
              )}
              <div className="rounded-lg bg-gray-50 border p-3 text-sm text-gray-700">
                On a {formatCurrency(samplePrice, currency)} ticket: attendee pays <b>{formatCurrency(sample.charged, currency)}</b>
                {draft.merchantId ? <>, {selected?.name || 'merchant'} {collect ? 'is owed' : 'receives'} <b>{formatCurrency(sample.merchantShare, currency)}</b></> : null}
                , platform fee <b>{formatCurrency(sample.fee, currency)}</b>.
                <span className="block text-xs text-gray-500 mt-1">{providerName}'s gateway charges are deducted from the platform account.</span>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.autoApproveOnPayment !== false}
                onChange={(e) => set({ autoApproveOnPayment: e.target.checked })} />
              Confirm registration and send the QR email as soon as payment succeeds
            </label>
          </>
        )}

        <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </CardContent>
    </Card>
  );
};

const TRANSFER_STYLE = {
  PENDING: 'bg-amber-100 text-amber-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  FAILED: 'bg-red-100 text-red-800',
  TRANSFERRED: 'bg-green-100 text-green-800',
};

/** Merchant shares held on the platform account for "collect, transfer later" events. */
const TransfersCard = ({ eventId, currency }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data } = useQuery({ queryKey: ['paymentTransfers', eventId], queryFn: () => ApiService.getPaymentTransfers(eventId) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['paymentTransfers', eventId] });
  const run = useMutation({
    mutationFn: () => ApiService.runPaymentTransfers(eventId),
    onSuccess: (r) => {
      refresh();
      const parts = [`${r.transferred} transferred`, r.processing && `${r.processing} processing`, r.failed && `${r.failed} failed`, r.skipped && `${r.skipped} skipped`].filter(Boolean);
      (r.failed ? showError : showSuccess)(`${parts.join(', ')}${r.messages?.length ? `. ${r.messages[0]}` : ''}`);
    },
    onError: (err) => showError(err?.message || 'Transfer failed'),
  });
  const manual = useMutation({
    mutationFn: ({ orderId, reference }) => ApiService.markPaymentTransferred(eventId, orderId, reference),
    onSuccess: () => { refresh(); showSuccess('Marked as transferred'); },
    onError: (err) => showError(err?.message || 'Could not update'),
  });
  if (!data || data.rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Merchant transfers</CardTitle>
        <p className="text-sm text-gray-600">
          Pending <b>{formatCurrency(data.pending, currency)}</b> ({data.pendingCount}) · transferred <b>{formatCurrency(data.transferred, currency)}</b>
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.pendingCount > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => window.confirm(`Transfer ${formatCurrency(data.pending, currency)} to merchants through the payment gateway?`) && run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Transferring…' : 'Transfer pending via gateway'}
            </Button>
            <span className="text-xs text-gray-500">Paid outside the gateway? Use "Mark paid" on the row and enter the bank/UPI reference.</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Registration</th>
                <th className="py-2 pr-3">Merchant</th>
                <th className="py-2 pr-3 text-right">Paid</th>
                <th className="py-2 pr-3 text-right">Share</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.orderId} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-3">
                    <div className="font-mono text-xs">{row.userId}</div>
                    <div className="text-xs text-gray-500">{row.provider} · {formatDateTime(row.paidAt)}</div>
                  </td>
                  <td className="py-2 pr-3">{row.merchantName || '—'}</td>
                  <td className="py-2 pr-3 text-right">{formatCurrency(row.orderAmount, currency)}</td>
                  <td className="py-2 pr-3 text-right font-medium">{formatCurrency(row.vendorAmount, currency)}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${TRANSFER_STYLE[row.status] || 'bg-gray-100 text-gray-700'}`}>{row.status.toLowerCase()}</span>
                    {row.reference && <div className="text-xs text-gray-500 mt-1 break-all">{row.mode === 'MANUAL' ? 'Ref ' : ''}{row.reference}</div>}
                    {row.error && <div className="text-xs text-red-600 mt-1">{row.error}</div>}
                  </td>
                  <td className="py-2 text-right">
                    {(row.status === 'PENDING' || row.status === 'FAILED') && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={manual.isPending}
                        onClick={() => {
                          const reference = window.prompt(`Bank/UPI reference for the ${formatCurrency(row.vendorAmount, currency)} you paid ${row.merchantName || 'the merchant'}`);
                          if (reference?.trim()) manual.mutate({ orderId: row.orderId, reference });
                        }}>
                        Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const EventPaymentSettings = () => {
  const { eventId } = useEventRoute();
  const navigate = useNavigate();
  const [isEditingOffline, setIsEditingOffline] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });
  const { data: paymentView, isLoading: configLoading } = useQuery({
    queryKey: ['eventPaymentConfig', eventId],
    queryFn: () => ApiService.getEventPaymentConfig(eventId),
    enabled: !!eventId,
  });
  const { data: merchants = [] } = useQuery({ queryKey: ['paymentMerchants'], queryFn: () => ApiService.listPaymentMerchants() });
  const { data: platform } = useQuery({ queryKey: ['paymentPlatform'], queryFn: () => ApiService.getPaymentPlatformStatus() });
  const { data: eventPaymentSettings, isLoading: eventSettingsLoading } = useQuery({
    queryKey: ['eventPaymentSettings', eventId],
    queryFn: () => ApiService.getEventPaymentSettings(eventId),
    enabled: !!eventId,
  });
  const { data: effectivePaymentSettings, isLoading: effectiveSettingsLoading } = useQuery({
    queryKey: ['effectivePaymentSettings', eventId],
    queryFn: () => ApiService.getEffectivePaymentSettings(eventId),
    enabled: !!eventId,
  });

  const createOrUpdateMutation = useMutation({
    mutationFn: (settingsData) => ApiService.createOrUpdateEventPaymentSettings(eventId, settingsData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventPaymentSettings', eventId] });
      queryClient.invalidateQueries({ queryKey: ['effectivePaymentSettings', eventId] });
      setIsEditingOffline(false);
      setShowCreateForm(false);
      showSuccess('Offline payment settings saved');
    },
    onError: () => showError('Failed to save offline payment settings'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ApiService.deleteEventPaymentSettings(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventPaymentSettings', eventId] });
      queryClient.invalidateQueries({ queryKey: ['effectivePaymentSettings', eventId] });
      showSuccess('Offline payment settings deleted');
    },
    onError: () => showError('Failed to delete settings'),
  });

  const isConfigured =
    eventPaymentSettings &&
    eventPaymentSettings.bank?.name &&
    eventPaymentSettings.bank?.accountNo &&
    eventPaymentSettings.bank?.ifsc &&
    eventPaymentSettings.upi;

  const isEffectiveConfigured =
    effectivePaymentSettings &&
    effectivePaymentSettings.bank?.name &&
    effectivePaymentSettings.bank?.accountNo &&
    effectivePaymentSettings.bank?.ifsc &&
    effectivePaymentSettings.upi;

  if (eventLoading || eventSettingsLoading || effectiveSettingsLoading || configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={() => navigate('/admin/events')} className="flex items-center space-x-2">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Events</span>
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Settings</h1>
          <p className="text-gray-600">{event?.name}</p>
        </div>
      </div>

      <CollectionCard eventId={eventId} event={event} view={paymentView} merchants={merchants} platform={platform} />
      <TransfersCard eventId={eventId} currency={event?.currency || 'INR'} />

      {/* Offline bank/UPI — used when mode is OFFLINE; keep editable so you can switch modes later */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Offline payment details</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  {isConfigured ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700 font-medium">Configured</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                      <span className="text-yellow-700 font-medium">Not configured</span>
                    </>
                  )}
                </div>

                {isConfigured ? (
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      <strong>Bank:</strong> {eventPaymentSettings.bank?.name}
                    </p>
                    <p>
                      <strong>Account:</strong> {eventPaymentSettings.bank?.accountNo}
                    </p>
                    <p>
                      <strong>UPI:</strong> {eventPaymentSettings.upi}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Required when collection mode is Offline.
                  </p>
                )}

                <div className="flex space-x-2 mt-4">
                  {isConfigured ? (
                    <>
                      <Button onClick={() => setIsEditingOffline(true)} size="sm">
                        Edit
                      </Button>
                      <Button
                        onClick={() => {
                          if (
                            window.confirm(
                              'Delete event-specific offline settings? Global fallback will be used if any.'
                            )
                          ) {
                            deleteMutation.mutate();
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => {
                        setShowCreateForm(true);
                        setIsEditingOffline(true);
                      }}
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create offline settings
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Active offline settings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEffectiveConfigured ? (
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      <strong>Bank:</strong> {effectivePaymentSettings.bank?.name}
                    </p>
                    <p>
                      <strong>Account:</strong> {effectivePaymentSettings.bank?.accountNo}
                    </p>
                    <p>
                      <strong>UPI:</strong> {effectivePaymentSettings.upi}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No offline settings configured.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {(isEditingOffline || showCreateForm) && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {showCreateForm ? 'Create offline payment settings' : 'Edit offline payment settings'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentSettingsForm
                  initialSettings={showCreateForm ? null : eventPaymentSettings}
                  onSave={(settingsData) =>
                    createOrUpdateMutation.mutate({
                      ...settingsData,
                      eventId,
                      isGlobal: false,
                    })
                  }
                  onCancel={() => {
                    setIsEditingOffline(false);
                    setShowCreateForm(false);
                  }}
                  isLoading={createOrUpdateMutation.isPending}
                />
              </CardContent>
            </Card>
          )}
    </div>
  );
};

export default EventPaymentSettings;
